import React, { useState, useMemo, useEffect, useCallback } from "react";
import { flushSync } from "react-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Alert,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  CircularProgress,
  Stack,
  Divider,
  Checkbox,
  FormHelperText,
  FormGroup,
  FormLabel,
} from "@mui/material";
import {
  Close as CloseIcon,
  Save as SaveIcon,
  TouchApp as TouchAppIcon,
  Reply as ReplyIcon,
  OpenInNew as OpenInNewIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import ImageIcon from "@mui/icons-material/Image";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import DescriptionIcon from "@mui/icons-material/Description";
import ServicesService from "../services/ServicesService";
import { buildButtonVariableSamplesFromCta } from "../utils/templateVariableSamples";
import { TEMPLATE_APP_USAGE_OPTIONS, parseTemplateAppUsages, toggleTemplateAppUsage } from "../utils/templateAppUsage";

const META_DOCS_TEMPLATES =
  "https://developers.facebook.com/docs/whatsapp/business-management-api/message-templates";

const TEMPLATE_LIMITS = {
  headerText: 60,
  bodyText: 1024,
  footerText: 60,
  ctaLabel: 25,
  copyCodeLabel: 25,
  quickReplyLabel: 25,
  mediaSizeBytes: {
    image: 5 * 1024 * 1024,
    video: 16 * 1024 * 1024,
    document: 100 * 1024 * 1024,
  },
};

const QUICK_REPLY_MAX = 3;
const CTA_URL_MAX = 2;
const CAROUSEL_CARD_MIN = 2;
const CAROUSEL_CARD_MAX = 10;
const CAROUSEL_BUTTON_MAX = 2;
const PRODUCT_MULTI_MAX = 30;
const PRODUCT_CAROUSEL_MAX = 10;

const isImageUrl = (url = "") => /\.(png|jpe?g|webp|gif)(\?.*)?$/i.test(url);
const isVideoUrl = (url = "") => /\.(mp4|mov|webm|m4v)(\?.*)?$/i.test(url);

const WIZARD_STEPS = [
  { label: "Setup", description: "Name, category, language, where to use" },
  { label: "Message", description: "Header, body, footer" },
  { label: "Actions", description: "Buttons or copy code" },
];

const CreateTemplateDialog = ({ open, onClose, onSave, initialValues, isEdit = false }) => {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    templateName: "",
    category: "",
    appUsages: ["draft"],
    language: "",
    headerType: "text",
    headerText: "",
    headerMediaType: "",
    headerMediaFile: null,
    body: "",
    footer: "",
    mediaUrl: null,
    mediaId: null,
    templateId: null,
    id: null,
  });

  const [headerVariableSamples, setHeaderVariableSamples] = useState({});
  const [bodyVariableSamples, setBodyVariableSamples] = useState({});
  const [footerVariableSamples, setFooterVariableSamples] = useState({});

  const [ctaButtons, setCtaButtons] = useState([]);
  const [quickReplyButtons, setQuickReplyButtons] = useState([]);
  /** 'cta' | 'quick' | 'none' — Meta does not allow mixing CTA and quick reply in one template */
  const [buttonGroup, setButtonGroup] = useState("none");
  const [copyCodeLabel, setCopyCodeLabel] = useState("");
  const [otpType, setOtpType] = useState("copy_code");
  const [autofillText, setAutofillText] = useState("");
  const [codeExpirationMinutes, setCodeExpirationMinutes] = useState("");
  const [addSecurityRecommendation, setAddSecurityRecommendation] = useState(false);
  const [supportedApps, setSupportedApps] = useState([{ package_name: "", signature_hash: "" }]);
  const [templateLayout, setTemplateLayout] = useState("standard");
  const [catalogId, setCatalogId] = useState("");
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [productDialogSearch, setProductDialogSearch] = useState("");
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [selectedMultiProductIds, setSelectedMultiProductIds] = useState([]);
  const [selectedProductCarouselIds, setSelectedProductCarouselIds] = useState([]);
  const [carouselCards, setCarouselCards] = useState([
    { header_format: "image", media_url: "", mediaFile: null, buttons: [{ type: "website", label: "", value: "", urlExample: "" }] },
    { header_format: "image", media_url: "", mediaFile: null, buttons: [{ type: "website", label: "", value: "", urlExample: "" }] },
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [syncToMeta, setSyncToMeta] = useState(true);

  const isAuthCategory = formData.category === "One-time Password"
    || formData.category === "Authentication";
  const isMarketingCategory = formData.category === "Marketing";

  const categories = [
    "Marketing",
    "Utility",
    "One-time Password",
    "Authentication",
  ];

  const LANGUAGES = [
    { name: "English (United States)", code: "en_US" },
    { name: "English (United Kingdom)", code: "en_GB" },
    { name: "Spanish (Spain)", code: "es_ES" },
    { name: "Spanish (Mexico)", code: "es_MX" },
    { name: "French (France)", code: "fr_FR" },
    { name: "French (Canada)", code: "fr_CA" },
    { name: "Chinese (Simplified)", code: "zh_CN" },
    { name: "Chinese (Traditional)", code: "zh_TW" },
    { name: "Arabic (Egypt)", code: "ar_EG" },
    { name: "Arabic (Saudi Arabia)", code: "ar_SA" },
    { name: "German (Germany)", code: "de_DE" },
    { name: "German (Austria)", code: "de_AT" },
    { name: "Italian (Italy)", code: "it_IT" },
    { name: "Italian (Switzerland)", code: "it_CH" },
    { name: "Russian (Russia)", code: "ru_RU" },
    { name: "Japanese (Japan)", code: "ja_JP" },
    { name: "Korean (South Korea)", code: "ko_KR" },
    { name: "Hindi (India)", code: "hi_IN" },
    { name: "Bengali (India)", code: "bn_IN" },
    { name: "Dutch (Netherlands)", code: "nl_NL" },
    { name: "Dutch (Belgium)", code: "nl_BE" },
    { name: "Polish (Poland)", code: "pl_PL" },
    { name: "Swedish (Sweden)", code: "sv_SE" },
    { name: "Norwegian (Norway)", code: "no_NO" },
    { name: "Thai (Thailand)", code: "th_TH" },
    { name: "Vietnamese (Vietnam)", code: "vi_VN" },
    { name: "Indonesian (Indonesia)", code: "id_ID" },
    { name: "Malay (Malaysia)", code: "ms_MY" },
    { name: "Romanian (Romania)", code: "ro_RO" },
    { name: "Czech (Czech Republic)", code: "cs_CZ" },
    { name: "Hungarian (Hungary)", code: "hu_HU" },
    { name: "Hebrew (Israel)", code: "he_IL" },
    { name: "Ukrainian (Ukraine)", code: "uk_UA" },
    { name: "Finnish (Finland)", code: "fi_FI" },
    { name: "Swahili (Kenya)", code: "sw_KE" },
    { name: "Tamil (India)", code: "ta_IN" },
    { name: "Telugu (India)", code: "te_IN" },
    { name: "Zulu (South Africa)", code: "zu_ZA" },
    { name: "Xhosa (South Africa)", code: "xh_ZA" },
    { name: "Filipino (Philippines)", code: "tl_PH" },
  ];

  const mediaTypes = [
    { value: "image", label: "Image", icon: <ImageIcon /> },
    { value: "video", label: "Video", icon: <VideoFileIcon /> },
    { value: "document", label: "Document", icon: <DescriptionIcon /> },
  ];

  const splitButtonsFromStore = useCallback((list) => {
    const all = Array.isArray(list) ? list : [];
    const quick = all.filter((b) => String(b?.type).toLowerCase() === "quick_reply");
    const cta = all.filter((b) => ["phone", "website"].includes(String(b?.type).toLowerCase()));
    return { quick, cta };
  }, []);

  const fetchCatalogProducts = useCallback(async () => {
    try {
      const wa = await ServicesService.getWhatsapp();
      const id = wa?.getCatalogId ? wa.getCatalogId() : null;
      setCatalogId(id || "");
      if (id) {
        const products = await ServicesService.getCatalogProducts(id);
        setCatalogProducts(Array.isArray(products) ? products : []);
      } else {
        setCatalogProducts([]);
      }
    } catch (e) {
      setCatalogId("");
      setCatalogProducts([]);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchCatalogProducts();
      setStep(0);
      if (initialValues && initialValues.attrs) {
        const a = initialValues.attrs;
        const headerFromStore = a.headerType
          || (a.headerMediaType ? "media" : "text");
        const stored = a.ctaButtons || [];
        const { quick, cta } = splitButtonsFromStore(stored);
        let inferredGroup = "none";
        if (quick.length) inferredGroup = "quick";
        else if (cta.length) inferredGroup = "cta";

        setFormData({
          templateName: a.templateName || "",
          category: a.category || "",
          appUsages: parseTemplateAppUsages(a.appUsages || a.appUsage, ["draft"]),
          language: a.language || "",
          headerType: headerFromStore,
          headerText: typeof a.headerText === "string" ? a.headerText : "",
          headerMediaType: a.headerMediaType || "",
          mediaUrl: a.mediaUrl,
          mediaId: a.mediaId,
          headerMediaFile: null,
          body: typeof a.body === "string" ? a.body : "",
          footer: typeof a.footer === "string" ? a.footer : "",
          templateId: a.templateId,
          id: a.id,
        });

        let variableSamples = { header: {}, body: {}, footer: {} };
        const rawVs = a.variableSamples;
        try {
          if (typeof rawVs === "string" && rawVs) {
            variableSamples = JSON.parse(rawVs);
          } else if (rawVs && typeof rawVs === "object") {
            variableSamples = rawVs;
          }
        } catch (e) {
          variableSamples = { header: {}, body: {}, footer: {} };
        }
        setHeaderVariableSamples(variableSamples.header || {});
        setBodyVariableSamples(variableSamples.body || {});
        setFooterVariableSamples(variableSamples.footer || {});

        setCtaButtons(
          cta.map((b) => ({
            type: b.type,
            label: b.label || "",
            value: b.value || "",
            urlExample: b.urlExample || "",
          })),
        );
        setQuickReplyButtons(quick.length ? quick.map((b) => ({ label: b.label || "" })) : [{ label: "" }]);
        setButtonGroup(
          a.category === "One-time Password" || a.category === "Authentication"
            ? "none"
            : inferredGroup,
        );
        setCopyCodeLabel(a.copyCodeLabel || "");
        setOtpType(a.otpType || a.otp_type || "copy_code");
        setAutofillText(a.autofill_text || "");
        setCodeExpirationMinutes(a.code_expiration_minutes != null ? String(a.code_expiration_minutes) : "");
        setAddSecurityRecommendation(Boolean(a.add_security_recommendation));
        const apps = Array.isArray(a.supported_apps) && a.supported_apps.length
          ? a.supported_apps
          : [{ package_name: "", signature_hash: "" }];
        setSupportedApps(apps.map((x) => ({
          package_name: x.package_name || "",
          signature_hash: x.signature_hash || "",
        })));
        setTemplateLayout(a.template_layout || "standard");
        setCatalogId(a.catalog_id || "");
        setSelectedProductIds(Array.isArray(a.product_retailer_ids) ? a.product_retailer_ids.slice(0, 1) : []);
        setSelectedMultiProductIds(Array.isArray(a.product_retailer_ids) ? a.product_retailer_ids : []);
        setSelectedProductCarouselIds(Array.isArray(a.product_retailer_ids) ? a.product_retailer_ids : []);
        const storedCards = Array.isArray(a.carousel_cards) ? a.carousel_cards : [];
        if (storedCards.length) {
          setCarouselCards(storedCards.map((c) => ({
            header_format: c.header_format || "image",
            media_url: c.media_url || "",
            mediaFile: null,
            buttons: Array.isArray(c.buttons) && c.buttons.length
              ? c.buttons.map((b) => ({
                type: b.type || "website",
                label: b.label || "",
                value: b.value || "",
                urlExample: b.urlExample || "",
              }))
              : [{ type: "website", label: "", value: "", urlExample: "" }],
          })));
        } else {
          setCarouselCards([
            { header_format: "image", media_url: "", mediaFile: null, buttons: [{ type: "website", label: "", value: "", urlExample: "" }] },
            { header_format: "image", media_url: "", mediaFile: null, buttons: [{ type: "website", label: "", value: "", urlExample: "" }] },
          ]);
        }
        setSyncToMeta(true);
      } else {
        setFormData({
          templateName: "",
          category: "",
          appUsages: ["draft"],
          language: "",
          headerType: "text",
          headerText: "",
          headerMediaType: "",
          headerMediaFile: null,
          body: "",
          footer: "",
          mediaUrl: null,
          mediaId: null,
          templateId: null,
          id: null,
        });
        setHeaderVariableSamples({});
        setBodyVariableSamples({});
        setFooterVariableSamples({});
        setCtaButtons([]);
        setQuickReplyButtons([{ label: "" }]);
        setButtonGroup("none");
        setCopyCodeLabel("");
        setOtpType("copy_code");
        setAutofillText("");
        setCodeExpirationMinutes("");
        setAddSecurityRecommendation(false);
        setSupportedApps([{ package_name: "", signature_hash: "" }]);
        setTemplateLayout("standard");
        setCatalogId("");
        setProductSearch("");
        setSelectedProductIds([]);
        setSelectedMultiProductIds([]);
        setSelectedProductCarouselIds([]);
        setCarouselCards([
          { header_format: "image", media_url: "", mediaFile: null, buttons: [{ type: "website", label: "", value: "", urlExample: "" }] },
          { header_format: "image", media_url: "", mediaFile: null, buttons: [{ type: "website", label: "", value: "", urlExample: "" }] },
        ]);
        setSyncToMeta(true);
      }
    }
  }, [open, initialValues, splitButtonsFromStore, fetchCatalogProducts]);

  const [errors, setErrors] = useState({});

  const extractVariables = (text) => {
    const source = typeof text === "string" ? text : "";
    const regex = /{{\s*([a-zA-Z0-9_]+)\s*}}/g;
    const variables = [];
    let match;
    while ((match = regex.exec(source))) {
      variables.push(match[1]);
    }
    return variables;
  };

  const getVariableType = (variables) => {
    if (variables.length === 0) return null;
    const isNumbered = variables.every((v) => /^\d+$/.test(v));
    const isNamed = variables.every((v) => /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(v));
    if (isNumbered) return "numbered";
    if (isNamed) return "named";
    return "mixed";
  };

  const effectiveHeaderType = isAuthCategory ? "none" : formData.headerType;

  const bodyVariables = useMemo(() => extractVariables(formData.body), [formData.body]);
  const headerVariables = useMemo(
    () => extractVariables(formData.headerText),
    [formData.headerText],
  );
  const footerVariables = useMemo(() => extractVariables(formData.footer), [formData.footer]);
  const variableType = useMemo(() => getVariableType(bodyVariables), [bodyVariables]);
  const filteredCatalogProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    if (!q) return catalogProducts;
    return catalogProducts.filter((p) => {
      const pid = String(p.catalog_product_id || p.product_id || p.id || p.retailer_id || "");
      const name = String(p.name || p.title || p.product_name || "");
      const sku = String(p.sku || p.product_sku || "");
      return pid.toLowerCase().includes(q)
        || name.toLowerCase().includes(q)
        || sku.toLowerCase().includes(q);
    });
  }, [catalogProducts, productSearch]);
  const activeProductIds = useMemo(
    () => (templateLayout === "product_single"
      ? selectedProductIds
      : templateLayout === "product_multi"
        ? selectedMultiProductIds
        : selectedProductCarouselIds),
    [templateLayout, selectedProductIds, selectedMultiProductIds, selectedProductCarouselIds],
  );
  const dialogFilteredCatalogProducts = useMemo(() => {
    const q = productDialogSearch.trim().toLowerCase();
    if (!q) return catalogProducts;
    return catalogProducts.filter((p) => {
      const pid = String(p.catalog_product_id || p.product_id || p.id || p.retailer_id || "");
      const name = String(p.name || p.title || p.product_name || "");
      const sku = String(p.sku || p.product_sku || "");
      return pid.toLowerCase().includes(q)
        || name.toLowerCase().includes(q)
        || sku.toLowerCase().includes(q);
    });
  }, [catalogProducts, productDialogSearch]);

  useEffect(() => {
    if (!isMarketingCategory && templateLayout !== "standard") {
      setTemplateLayout("standard");
    }
  }, [isMarketingCategory, templateLayout]);

  const handleInputChange = (field, value) => {
    if (field === "headerType" && value === "media") {
      setFormData((prev) => ({
        ...prev,
        headerType: value,
        headerText: "",
      }));
    } else if (field === "headerType" && value === "none") {
      setFormData((prev) => ({
        ...prev,
        headerType: "none",
        headerText: "",
        headerMediaType: "",
        headerMediaFile: null,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
    if (field === "headerText") {
      const vars = extractVariables(value);
      setHeaderVariableSamples((prev) => {
        const updated = {};
        vars.forEach((v) => {
          updated[v] = prev[v] || "";
        });
        return updated;
      });
    }
    if (field === "body") {
      const vars = extractVariables(value);
      setBodyVariableSamples((prev) => {
        const updated = {};
        vars.forEach((v) => {
          updated[v] = prev[v] || "";
        });
        return updated;
      });
    }
    if (field === "footer") {
      const vars = extractVariables(value);
      setFooterVariableSamples((prev) => {
        const updated = {};
        vars.forEach((v) => {
          updated[v] = prev[v] || "";
        });
        return updated;
      });
    }
  };

  /** Validate only wizard step 0 — template identity */
  const validateStep0 = () => {
    const newErrors = {};
    if (!String(formData.templateName || "").trim()) {
      newErrors.templateName = "Template name is required";
    }
    if (!formData.category) {
      newErrors.category = "Category is required";
    }
    if (!formData.language) {
      newErrors.language = "Language is required";
    }
    setErrors((prev) => {
      const next = { ...prev };
      delete next.templateName;
      delete next.category;
      delete next.language;
      return { ...next, ...newErrors };
    });
    return Object.keys(newErrors).length === 0;
  };

  /** Validate only wizard step 1 — header, body, footer */
  const validateStep1 = () => {
    const newErrors = {};

    if (effectiveHeaderType === "text" && String(formData.headerText || "").length > TEMPLATE_LIMITS.headerText) {
      newErrors.headerText = `Header text cannot exceed ${TEMPLATE_LIMITS.headerText} characters`;
    }
    if (effectiveHeaderType === "text" && headerVariables.length > 1) {
      newErrors.headerText = "Only one variable is allowed in header.";
    }
    if (effectiveHeaderType === "text" && headerVariables.length === 1) {
      const v = headerVariables[0];
      if (!headerVariableSamples[v] || !headerVariableSamples[v].trim()) {
        newErrors[`variable_header_${v}`] = `Sample text for '{{${v}}}' in header is required.`;
      }
    }
    if (effectiveHeaderType === "media" && formData.headerMediaFile) {
      const limit = TEMPLATE_LIMITS.mediaSizeBytes[formData.headerMediaType];
      if (limit && formData.headerMediaFile.size > limit) {
        const sizeMb = Math.floor(limit / (1024 * 1024));
        newErrors.headerMediaFile = `${formData.headerMediaType} size must be ${sizeMb} MB or less.`;
      }
    }

    if (formData.body && formData.body.length > TEMPLATE_LIMITS.bodyText) {
      newErrors.body = `Body text cannot exceed ${TEMPLATE_LIMITS.bodyText} characters`;
    }
    if (formData.footer && formData.footer.length > TEMPLATE_LIMITS.footerText) {
      newErrors.footer = `Footer text cannot exceed ${TEMPLATE_LIMITS.footerText} characters`;
    }
    if (footerVariables.length > 0) {
      newErrors.footer = "Variables are not allowed in footer.";
    }

    if (!String(formData.body || "").trim()) {
      newErrors.body = "Body content is required";
    } else {
      const curlyMatches = String(formData.body).match(/{{[^}]*}}/g) || [];
      const invalidCurly = curlyMatches.some(
        (v) => !/^{{\s*[a-zA-Z0-9_]+\s*}}$/.test(v),
      );
      if (invalidCurly) {
        newErrors.body = "Invalid variable format. Use {{name}} or {{number}}.";
      }
      if (bodyVariables.length > 0 && variableType === "mixed") {
        newErrors.body = "Do not mix named and numbered variables. Use only one type.";
      }
      if (bodyVariables.length > 0) {
        bodyVariables.forEach((v) => {
          if (!bodyVariableSamples[v] || !bodyVariableSamples[v].trim()) {
            newErrors[`variable_body_${v}`] = `Sample text for '{{${v}}}' in body is required.`;
          }
        });
      }
    }

    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(prev).forEach((k) => {
        if (
          k === "headerText"
          || k === "headerMediaFile"
          || k === "body"
          || k === "footer"
          || k.startsWith("variable_header_")
          || k.startsWith("variable_body_")
        ) {
          delete next[k];
        }
      });
      return { ...next, ...newErrors };
    });
    return Object.keys(newErrors).length === 0;
  };

  /** Validate only wizard step 2 — authentication copy code or interactive buttons */
  const validateStep2 = () => {
    const newErrors = {};

    if (isAuthCategory) {
      if (otpType === "copy_code" && !copyCodeLabel.trim()) {
        newErrors.copyCodeLabel = "Copy code label is required for authentication templates.";
      } else if (copyCodeLabel.trim().length > TEMPLATE_LIMITS.copyCodeLabel) {
        newErrors.copyCodeLabel = `Label cannot exceed ${TEMPLATE_LIMITS.copyCodeLabel} characters.`;
      }
      if (codeExpirationMinutes && (!/^\d+$/.test(codeExpirationMinutes) || Number(codeExpirationMinutes) < 1 || Number(codeExpirationMinutes) > 90)) {
        newErrors.codeExpirationMinutes = "Code expiration must be between 1 and 90 minutes.";
      }
      if (otpType === "one_tap" || otpType === "zero_tap") {
        const valid = supportedApps.filter((a) => a.package_name.trim() && a.signature_hash.trim());
        if (valid.length === 0) {
          newErrors.supportedApps = "Add at least one supported app (package name + signature hash).";
        }
      }
    } else if (templateLayout === "carousel_media") {
      if (formData.category !== "Marketing") {
        newErrors.templateLayout = "Carousel templates are supported only for Marketing category.";
      }
      if (carouselCards.length < CAROUSEL_CARD_MIN || carouselCards.length > CAROUSEL_CARD_MAX) {
        newErrors.carouselCards = `Carousel requires ${CAROUSEL_CARD_MIN}-${CAROUSEL_CARD_MAX} cards.`;
      }
      carouselCards.forEach((card, cIdx) => {
        if (!(card.mediaFile || (card.media_url && card.media_url.trim()))) {
          newErrors[`carousel_media_${cIdx}`] = "Card media URL is required.";
        }
        const btns = Array.isArray(card.buttons) ? card.buttons : [];
        if (!btns.length) {
          newErrors[`carousel_buttons_${cIdx}`] = "Add at least one button on this card.";
          return;
        }
        if (btns.length > CAROUSEL_BUTTON_MAX) {
          newErrors[`carousel_buttons_${cIdx}`] = `Each card supports up to ${CAROUSEL_BUTTON_MAX} buttons.`;
        }
        btns.forEach((btn, bIdx) => {
          if (!btn.label || !btn.label.trim()) {
            newErrors[`carousel_btn_label_${cIdx}_${bIdx}`] = "Button label is required.";
          } else if (btn.label.trim().length > TEMPLATE_LIMITS.ctaLabel) {
            newErrors[`carousel_btn_label_${cIdx}_${bIdx}`] = `Max ${TEMPLATE_LIMITS.ctaLabel} characters.`;
          }
          const type = btn.type || "website";
          if (type === "quick_reply") return;
          if (!btn.value || !btn.value.trim()) {
            newErrors[`carousel_btn_value_${cIdx}_${bIdx}`] = "Button value is required.";
            return;
          }
          if (type === "phone") {
            if (!/^\+?\d{7,}$/.test(btn.value.trim())) {
              newErrors[`carousel_btn_value_${cIdx}_${bIdx}`] = "Enter a valid phone number.";
            }
            return;
          }
          const u = btn.value.trim();
          if (!/^https?:\/\/.+/.test(u)) {
            newErrors[`carousel_btn_value_${cIdx}_${bIdx}`] = "Enter a valid https URL.";
          }
          const varCount = (u.match(/\{\{1\}\}/g) || []).length;
          if (varCount > 1) {
            newErrors[`carousel_btn_value_${cIdx}_${bIdx}`] = "Only one {{1}} segment is allowed in the URL.";
          }
          if (varCount === 1 && !(btn.urlExample && btn.urlExample.trim())) {
            newErrors[`carousel_btn_example_${cIdx}_${bIdx}`] = "Sample URL is required when using {{1}}.";
          }
        });
      });
    } else if (templateLayout === "product_single") {
      if (formData.category !== "Marketing") {
        newErrors.templateLayout = "Product templates are supported only for Marketing category.";
      }
      if (!catalogId) newErrors.catalogId = "Catalog ID is required.";
      if (!selectedProductIds.length) newErrors.productSingle = "Select one product.";
    } else if (templateLayout === "product_multi") {
      if (formData.category !== "Marketing") {
        newErrors.templateLayout = "Product templates are supported only for Marketing category.";
      }
      if (!catalogId) newErrors.catalogId = "Catalog ID is required.";
      if (selectedMultiProductIds.length < 2) newErrors.productMulti = "Select at least two products.";
      if (selectedMultiProductIds.length > PRODUCT_MULTI_MAX) newErrors.productMulti = `You can select up to ${PRODUCT_MULTI_MAX} products.`;
    } else if (templateLayout === "product_carousel") {
      if (formData.category !== "Marketing") {
        newErrors.templateLayout = "Product templates are supported only for Marketing category.";
      }
      if (!catalogId) newErrors.catalogId = "Catalog ID is required.";
      if (selectedProductCarouselIds.length < 2) newErrors.productCarousel = "Select at least two products.";
      if (selectedProductCarouselIds.length > PRODUCT_CAROUSEL_MAX) newErrors.productCarousel = `Product carousel supports up to ${PRODUCT_CAROUSEL_MAX} products/cards.`;
    } else if (buttonGroup === "quick") {
      const filled = quickReplyButtons.filter((b) => b.label && b.label.trim());
      if (filled.length === 0) {
        newErrors.quickReply = "Add at least one quick reply label, or set button type to None.";
      }
      filled.forEach((b) => {
        const raw = quickReplyButtons.indexOf(b);
        if (b.label.trim().length > TEMPLATE_LIMITS.quickReplyLabel) {
          newErrors[`qr_${raw}`] = `Max ${TEMPLATE_LIMITS.quickReplyLabel} characters.`;
        }
      });
      if (filled.length > QUICK_REPLY_MAX) {
        newErrors.quickReply = `Meta allows at most ${QUICK_REPLY_MAX} quick reply buttons.`;
      }
    } else if (buttonGroup === "cta") {
      const phoneCount = ctaButtons.filter((b) => b.type === "phone").length;
      const urlCount = ctaButtons.filter((b) => b.type === "website").length;
      if (phoneCount > 1) newErrors.cta = "At most one phone button is allowed.";
      if (urlCount > CTA_URL_MAX) newErrors.cta = `At most ${CTA_URL_MAX} website buttons are allowed.`;

      ctaButtons.forEach((btn, idx) => {
        if (!btn.label || !btn.label.trim()) {
          newErrors[`cta_label_${idx}`] = "Button label is required.";
        } else if (btn.label.trim().length > TEMPLATE_LIMITS.ctaLabel) {
          newErrors[`cta_label_${idx}`] = `Max ${TEMPLATE_LIMITS.ctaLabel} characters.`;
        }
        if (!btn.value || !btn.value.trim()) {
          newErrors[`cta_value_${idx}`] = "Value is required.";
        } else if (btn.type === "phone" && !/^\+?\d{7,}$/.test(btn.value.trim())) {
          newErrors[`cta_value_${idx}`] = "Enter a valid phone number with country code.";
        } else if (btn.type === "website") {
          const u = btn.value.trim();
          if (!/^https?:\/\/.+\{\{1\}\}/.test(u) && !/^https?:\/\/.+/.test(u)) {
            newErrors[`cta_value_${idx}`] = "Enter a valid https URL.";
          }
          const varCount = (u.match(/\{\{1\}\}/g) || []).length;
          if (varCount > 1) {
            newErrors[`cta_value_${idx}`] = "Only one {{1}} segment is allowed in the URL.";
          }
          if (varCount === 1 && !(btn.urlExample && btn.urlExample.trim())) {
            newErrors[`cta_example_${idx}`] = "Provide a sample full URL for Meta review (e.g. https://shop.com/track/abc).";
          }
        }
      });
    }

    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(prev).forEach((k) => {
        if (
          k === "copyCodeLabel"
          || k === "quickReply"
          || k.startsWith("qr_")
          || k === "cta"
          || k.startsWith("cta_label_")
          || k.startsWith("cta_value_")
          || k.startsWith("cta_example_")
          || k === "codeExpirationMinutes"
          || k === "supportedApps"
          || k === "templateLayout"
          || k === "carouselCards"
          || k.startsWith("carousel_media_")
          || k.startsWith("carousel_buttons_")
          || k.startsWith("carousel_btn_label_")
          || k.startsWith("carousel_btn_value_")
          || k.startsWith("carousel_btn_example_")
          || k === "catalogId"
          || k === "productSingle"
          || k === "productMulti"
          || k === "productCarousel"
        ) {
          delete next[k];
        }
      });
      return { ...next, ...newErrors };
    });
    return Object.keys(newErrors).length === 0;
  };

  const validateForm = () => {
    let ok0 = true;
    let ok1 = true;
    let ok2 = true;
    flushSync(() => {
      ok0 = validateStep0();
    });
    flushSync(() => {
      ok1 = validateStep1();
    });
    flushSync(() => {
      ok2 = validateStep2();
    });
    return ok0 && ok1 && ok2;
  };

  const handleNext = () => {
    if (step === 0 && !validateStep0()) return;
    if (step === 1 && !validateStep1()) return;
    setStep((s) => Math.min(s + 1, WIZARD_STEPS.length - 1));
  };

  const handleBack = () => {
    setStep((s) => Math.max(s - 1, 0));
  };

  const buildTemplateButtons = () => {
    if (isAuthCategory) return [];
    if (buttonGroup === "quick") {
      return quickReplyButtons
        .filter((b) => b.label && b.label.trim())
        .map((b) => ({ type: "quick_reply", label: b.label.trim(), value: "" }));
    }
    if (buttonGroup === "cta") {
      return ctaButtons.map((b) => {
        const row = {
          type: b.type,
          label: (b.label || "").trim(),
          value: (b.value || "").trim(),
        };
        if (b.type === "website" && b.urlExample && b.urlExample.trim()) {
          row.urlExample = b.urlExample.trim();
        }
        return row;
      });
    }
    return [];
  };

  const handleSaveAppUsage = async () => {
    if (!formData.id) return;
    setIsSubmitting(true);
    try {
      await onSave({
        id: formData.id,
        appUsages: parseTemplateAppUsages(formData.appUsages, ["draft"]),
        onlyAppUsage: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const templateButtons = buildTemplateButtons();
      await onSave({
        ...formData,
        headerType: effectiveHeaderType,
        variableSamples: {
          header: headerVariableSamples,
          body: bodyVariableSamples,
          footer: footerVariableSamples,
          buttons: buildButtonVariableSamplesFromCta(buttonGroup === "cta" ? ctaButtons : []),
        },
        ctaButtons: buttonGroup === "cta" ? ctaButtons : [],
        quickReplyButtons: buttonGroup === "quick" ? quickReplyButtons.filter((b) => b.label && b.label.trim()) : [],
        templateButtons,
        copyCodeLabel,
        otpType,
        autofillText,
        codeExpirationMinutes,
        addSecurityRecommendation,
        supportedApps: supportedApps.filter((a) => a.package_name.trim() && a.signature_hash.trim()),
        templateLayout,
        carouselCards,
        catalogId,
        productRetailerIds:
          templateLayout === "product_single"
            ? selectedProductIds.slice(0, 1)
            : templateLayout === "product_multi"
              ? selectedMultiProductIds
              : templateLayout === "product_carousel"
                ? selectedProductCarouselIds
                : [],
        multiProductSections:
          templateLayout === "product_multi"
            ? [{ title: "Products", product_retailer_ids: selectedMultiProductIds }]
            : [],
        syncToMeta: isEdit ? undefined : syncToMeta,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      templateName: "",
      category: "",
      appUsages: ["draft"],
      language: "",
      headerType: "text",
      headerText: "",
      headerMediaType: "",
      headerMediaFile: null,
      body: "",
      footer: "",
      mediaUrl: null,
      mediaId: null,
      templateId: null,
      id: null,
    });
    setCtaButtons([]);
    setQuickReplyButtons([{ label: "" }]);
    setButtonGroup("none");
    setStep(0);
    setOtpType("copy_code");
    setAutofillText("");
    setCodeExpirationMinutes("");
    setAddSecurityRecommendation(false);
    setSupportedApps([{ package_name: "", signature_hash: "" }]);
    setTemplateLayout("standard");
    setCatalogId("");
    setProductSearch("");
    setSelectedProductIds([]);
    setSelectedMultiProductIds([]);
    setSelectedProductCarouselIds([]);
    setCarouselCards([
      { header_format: "image", media_url: "", mediaFile: null, buttons: [{ type: "website", label: "", value: "", urlExample: "" }] },
      { header_format: "image", media_url: "", mediaFile: null, buttons: [{ type: "website", label: "", value: "", urlExample: "" }] },
    ]);
    onClose();
    setErrors({});
  };

  const maxSelectableProducts = templateLayout === "product_single"
    ? 1
    : templateLayout === "product_multi"
      ? PRODUCT_MULTI_MAX
      : PRODUCT_CAROUSEL_MAX;

  const toggleProductSelection = (pid) => {
    const id = String(pid);
    if (templateLayout === "product_single") {
      setSelectedProductIds([id]);
      return;
    }
    const current = templateLayout === "product_multi" ? selectedMultiProductIds : selectedProductCarouselIds;
    const exists = current.includes(id);
    let next = current;
    if (exists) next = current.filter((x) => x !== id);
    else if (current.length < maxSelectableProducts) next = [...current, id];
    if (templateLayout === "product_multi") setSelectedMultiProductIds(next);
    else setSelectedProductCarouselIds(next);
  };

  const renderSection = (title, description, children) => (
    <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
      <CardContent sx={{ p: 2.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {description}
          </Typography>
        )}
        {children}
      </CardContent>
    </Card>
  );

  const dialogTitle = isEdit ? "Edit message template" : "Create message template";
  const lastStepIndex = WIZARD_STEPS.length - 1;
  const isLastStep = step === lastStepIndex;

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (isSubmitting && (reason === "backdropClick" || reason === "escapeKeyDown")) {
          return;
        }
        handleClose();
      }}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "92vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          fontFamily: "Poppins, sans-serif",
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {dialogTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Structure matches WhatsApp Business Platform message templates. See{" "}
              <a href={META_DOCS_TEMPLATES} target="_blank" rel="noopener noreferrer">
                Meta template components
              </a>
              .
            </Typography>
          </Box>
          <Chip label="WhatsApp" size="small" color="success" variant="outlined" />
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: 2, pb: 1 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            mb: 2,
            py: 1.5,
            px: 2,
            borderRadius: 2,
            bgcolor: "action.hover",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
            Step {step + 1} of {WIZARD_STEPS.length}: {WIZARD_STEPS[step].label}
          </Typography>
          <Stack direction="row" spacing={0.5}>
            {WIZARD_STEPS.map((s, i) => (
              <Chip
                key={s.label}
                size="small"
                label={i + 1}
                color={i === step ? "primary" : i < step ? "success" : "default"}
                variant={i === step ? "filled" : "outlined"}
                sx={{ minWidth: 32, fontWeight: 700 }}
              />
            ))}
          </Stack>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
          {WIZARD_STEPS[step].description}
        </Typography>

        {step === 0 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Pick category, language, and where Grow should show this template. Language cannot change after Meta approves it.
          </Alert>
          {renderSection(
            "Template identity",
            "Internal name, Meta category, and which Grow screen can send this template.",
            <Stack spacing={2}>
              <TextField
                label="Template name"
                value={formData.templateName}
                onChange={(e) => handleInputChange("templateName", e.target.value)}
                disabled={!!isEdit}
                error={!!errors.templateName}
                helperText={errors.templateName || "Lowercase with underscores when synced (handled automatically)."}
                fullWidth
                size="small"
                required
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <FormControl fullWidth size="small" error={!!errors.category} required>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.category}
                    onChange={(e) => {
                      const v = e.target.value;
                      handleInputChange("category", v);
                      if (v === "One-time Password" || v === "Authentication") {
                        setButtonGroup("none");
                        setCtaButtons([]);
                        setQuickReplyButtons([{ label: "" }]);
                        handleInputChange("headerType", "none");
                      }
                    }}
                    label="Category"
                    disabled={!!isEdit}
                  >
                    {categories.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small" error={!!errors.language} required>
                  <InputLabel>Language</InputLabel>
                  <Select
                    value={formData.language}
                    onChange={(e) => handleInputChange("language", e.target.value)}
                    label="Language"
                    disabled={!!isEdit}
                  >
                    {LANGUAGES.map((lang) => (
                      <MenuItem key={lang.code} value={lang.code}>
                        {lang.name} ({lang.code})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <FormControl component="fieldset" variant="standard">
                <FormLabel component="legend" sx={{ fontSize: 14, mb: 0.5 }}>Show in</FormLabel>
                <FormGroup>
                  {TEMPLATE_APP_USAGE_OPTIONS.map((opt) => {
                    const selected = parseTemplateAppUsages(formData.appUsages, ["draft"]);
                    const checked = selected.includes(opt.value);
                    return (
                      <FormControlLabel
                        key={opt.value}
                        control={(
                          <Checkbox
                            size="small"
                            checked={checked}
                            onChange={() => handleInputChange(
                              "appUsages",
                              toggleTemplateAppUsage(formData.appUsages, opt.value),
                            )}
                          />
                        )}
                        label={(
                          <Box>
                            <Typography variant="body2">{opt.label}</Typography>
                            <Typography variant="caption" color="text.secondary">{opt.hint}</Typography>
                          </Box>
                        )}
                      />
                    );
                  })}
                </FormGroup>
                <FormHelperText>
                  New templates start as Draft. Check Inbox, Calls, or All screens when you want them in a picker.
                </FormHelperText>
              </FormControl>
              {!isEdit && (
                <Box sx={{ p: 2, borderRadius: 2, bgcolor: "grey.50", border: "1px solid", borderColor: "divider" }}>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={syncToMeta}
                        onChange={(e) => setSyncToMeta(e.target.checked)}
                        color="primary"
                      />
                    )}
                    label={<Typography fontWeight={600}>Submit to Meta after save</Typography>}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 0.5 }}>
                    Turn off to keep a local draft and sync later from the template list.
                  </Typography>
                </Box>
              )}
            </Stack>,
          )}
        </Box>
        )}

        {step === 1 && (
        <Box>
          {isAuthCategory && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Authentication templates use a fixed Copy Code button on Meta (no header media). Body should include
              {" "}
              <strong>{"{{1}}"}</strong>
              {" "}
              for the verification code.
            </Alert>
          )}

          {renderSection(
            "Header",
            isAuthCategory
              ? "Disabled for authentication — Meta approval uses body + copy code only."
              : "Optional. Text supports one variable; media uses your sample file for review.",
            <Stack spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Header format</InputLabel>
                <Select
                  value={effectiveHeaderType}
                  onChange={(e) => handleInputChange("headerType", e.target.value)}
                  label="Header format"
                  disabled={isAuthCategory}
                >
                  <MenuItem value="none">None</MenuItem>
                  <MenuItem value="text">Text</MenuItem>
                  <MenuItem value="media">Media (image / video / document)</MenuItem>
                </Select>
              </FormControl>

              {effectiveHeaderType === "text" && (
                <>
                  <TextField
                    label="Header text"
                    value={formData.headerText}
                    onChange={(e) => handleInputChange("headerText", e.target.value)}
                    error={!!errors.headerText}
                    helperText={
                      errors.headerText
                      || `${String(formData.headerText || "").length}/${TEMPLATE_LIMITS.headerText} · one variable max`
                    }
                    fullWidth
                    size="small"
                    inputProps={{ maxLength: TEMPLATE_LIMITS.headerText }}
                  />
                  {headerVariables.length === 1 && (
                    <TextField
                      label={`Sample for {{${headerVariables[0]}}}`}
                      value={headerVariableSamples[headerVariables[0]] || ""}
                      onChange={(e) => setHeaderVariableSamples((p) => ({
                        ...p,
                        [headerVariables[0]]: e.target.value,
                      }))}
                      error={!!errors[`variable_header_${headerVariables[0]}`]}
                      helperText={errors[`variable_header_${headerVariables[0]}`]}
                      fullWidth
                      size="small"
                    />
                  )}
                </>
              )}

              {effectiveHeaderType === "media" && !isAuthCategory && (
                <Stack spacing={2}>
                  <RadioGroup
                    value={formData.headerMediaType}
                    onChange={(e) => {
                      handleInputChange("headerMediaType", e.target.value);
                      setFormData((prev) => ({ ...prev, headerMediaFile: null }));
                    }}
                  >
                    {mediaTypes.map((type) => (
                      <FormControlLabel
                        key={type.value}
                        value={type.value}
                        control={<Radio size="small" />}
                        label={(
                          <Stack direction="row" alignItems="center" spacing={1}>
                            {type.icon}
                            <span>{type.label}</span>
                          </Stack>
                        )}
                      />
                    ))}
                  </RadioGroup>
                  <Button variant="outlined" component="label" size="small" sx={{ alignSelf: "flex-start" }}>
                    {formData.headerMediaFile ? "Change file" : "Upload sample file"}
                    <input
                      type="file"
                      accept={
                        formData.headerMediaType === "image"
                          ? ".jpg,.jpeg,.png"
                          : formData.headerMediaType === "video"
                            ? ".mp4"
                            : ".pdf"
                      }
                      hidden
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        const limit = TEMPLATE_LIMITS.mediaSizeBytes[formData.headerMediaType];
                        if (limit && file.size > limit) {
                          setErrors((p) => ({
                            ...p,
                            headerMediaFile: "File exceeds Meta size limit for this type.",
                          }));
                          return;
                        }
                        setErrors((p) => ({ ...p, headerMediaFile: "" }));
                        setFormData((prev) => ({ ...prev, headerMediaFile: file }));
                      }}
                    />
                  </Button>
                  {errors.headerMediaFile && (
                    <Typography variant="caption" color="error">{errors.headerMediaFile}</Typography>
                  )}
                  {(formData.headerMediaFile || (isEdit && formData.mediaUrl)) && (
                    <Typography variant="caption" color="text.secondary">
                      {formData.headerMediaFile?.name || "Saved media on template"}
                    </Typography>
                  )}
                </Stack>
              )}
            </Stack>,
          )}

          {renderSection(
            "Body",
            "Required. Use {{1}} or {{name}} placeholders — each needs a sample below for Meta review.",
            <Stack spacing={2}>
              <TextField
                label="Body"
                value={formData.body}
                onChange={(e) => handleInputChange("body", e.target.value)}
                error={!!errors.body}
                helperText={errors.body || `${String(formData.body || "").length}/${TEMPLATE_LIMITS.bodyText}`}
                fullWidth
                multiline
                rows={6}
                size="small"
                inputProps={{ maxLength: TEMPLATE_LIMITS.bodyText }}
                sx={{
                  "& .MuiInputBase-inputMultiline": {
                    resize: "vertical",
                    overflow: "auto",
                  },
                }}
              />
              {bodyVariables.length > 0 && (
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2" fontWeight={600}>Variable samples</Typography>
                  {bodyVariables.map((v) => (
                    <TextField
                      key={v}
                      label={`Sample for {{${v}}}`}
                      value={bodyVariableSamples[v] || ""}
                      onChange={(e) => setBodyVariableSamples((p) => ({ ...p, [v]: e.target.value }))}
                      error={!!errors[`variable_body_${v}`]}
                      helperText={errors[`variable_body_${v}`]}
                      fullWidth
                      size="small"
                    />
                  ))}
                </Stack>
              )}
            </Stack>,
          )}

          {renderSection(
            "Footer",
            "Optional static line. Variables are not allowed.",
            <TextField
              label="Footer"
              value={formData.footer}
              onChange={(e) => handleInputChange("footer", e.target.value)}
              error={!!errors.footer}
              helperText={errors.footer || `${String(formData.footer || "").length}/${TEMPLATE_LIMITS.footerText}`}
              fullWidth
              size="small"
              inputProps={{ maxLength: TEMPLATE_LIMITS.footerText }}
            />,
          )}
        </Box>
        )}

        {step === 2 && (
        <Box>
          {!isAuthCategory && isMarketingCategory && renderSection(
            "Template layout",
            "Marketing-only layouts: standard, media carousel, and catalog product templates.",
            <Stack spacing={1}>
              <RadioGroup
                value={templateLayout}
                onChange={(e) => setTemplateLayout(e.target.value)}
              >
                <FormControlLabel value="standard" control={<Radio />} label="Standard template" />
                <FormControlLabel value="carousel_media" control={<Radio />} label="Media carousel (Marketing only)" />
                <FormControlLabel value="product_single" control={<Radio />} label="Single product template (Marketing)" />
                <FormControlLabel value="product_multi" control={<Radio />} label="Multi-product template (Marketing)" />
                <FormControlLabel value="product_carousel" control={<Radio />} label="Product carousel template (Marketing)" />
              </RadioGroup>
              {errors.templateLayout && <Typography variant="caption" color="error">{errors.templateLayout}</Typography>}
            </Stack>,
          )}
          {isAuthCategory ? (
            renderSection(
              "Authentication options",
              "Supports Copy Code, One-Tap, and Zero-Tap (Android fallback handled by Meta).",
              <Stack spacing={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>OTP button type</InputLabel>
                  <Select value={otpType} onChange={(e) => setOtpType(e.target.value)} label="OTP button type">
                    <MenuItem value="copy_code">Copy code</MenuItem>
                    <MenuItem value="one_tap">One-tap autofill</MenuItem>
                    <MenuItem value="zero_tap">Zero-tap autofill</MenuItem>
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={addSecurityRecommendation}
                      onChange={(e) => setAddSecurityRecommendation(e.target.checked)}
                    />
                  )}
                  label="Add security recommendation in body"
                />
                <TextField
                  label="Code expiration minutes (optional)"
                  value={codeExpirationMinutes}
                  onChange={(e) => setCodeExpirationMinutes(e.target.value)}
                  error={!!errors.codeExpirationMinutes}
                  helperText={errors.codeExpirationMinutes || "1-90 minutes"}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="Copy code fallback label"
                  value={copyCodeLabel}
                  onChange={(e) => setCopyCodeLabel(e.target.value)}
                  error={!!errors.copyCodeLabel}
                  helperText={errors.copyCodeLabel || `${copyCodeLabel.length}/${TEMPLATE_LIMITS.copyCodeLabel}`}
                  fullWidth
                  size="small"
                  inputProps={{ maxLength: TEMPLATE_LIMITS.copyCodeLabel }}
                />
                {(otpType === "one_tap" || otpType === "zero_tap") && (
                  <>
                    <TextField
                      label="Autofill button text (optional)"
                      value={autofillText}
                      onChange={(e) => setAutofillText(e.target.value)}
                      size="small"
                      fullWidth
                    />
                    {errors.supportedApps && <Alert severity="error">{errors.supportedApps}</Alert>}
                    {supportedApps.map((app, idx) => (
                      <Stack key={idx} spacing={1}>
                        <TextField
                          label="Android package name"
                          value={app.package_name}
                          onChange={(e) => {
                            const next = [...supportedApps];
                            next[idx] = { ...next[idx], package_name: e.target.value };
                            setSupportedApps(next);
                          }}
                          size="small"
                          fullWidth
                        />
                        <TextField
                          label="App signature hash"
                          value={app.signature_hash}
                          onChange={(e) => {
                            const next = [...supportedApps];
                            next[idx] = { ...next[idx], signature_hash: e.target.value };
                            setSupportedApps(next);
                          }}
                          size="small"
                          fullWidth
                        />
                      </Stack>
                    ))}
                    <Button size="small" variant="outlined" onClick={() => setSupportedApps((p) => [...p, { package_name: "", signature_hash: "" }])}>
                      Add supported app
                    </Button>
                  </>
                )}
              </Stack>,
            )
          ) : (
            <>
              {isMarketingCategory && templateLayout === "carousel_media" ? (
                renderSection(
                  "Carousel cards",
                  "Each card needs media and one or more buttons (website, phone, or quick reply).",
                  <Stack spacing={2}>
                    {errors.carouselCards && <Alert severity="error">{errors.carouselCards}</Alert>}
                    {carouselCards.map((card, cIdx) => (
                      <Card key={cIdx} variant="outlined" sx={{ p: 2 }}>
                        <Stack spacing={2}>
                          <Stack direction="row" justifyContent="space-between">
                            <Chip size="small" label={`Card ${cIdx + 1}`} />
                            <IconButton
                              size="small"
                              onClick={() => setCarouselCards((prev) => prev.filter((_, i) => i !== cIdx))}
                              disabled={carouselCards.length <= CAROUSEL_CARD_MIN}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                          <FormControl size="small">
                            <InputLabel>Header format</InputLabel>
                            <Select
                              value={card.header_format || "image"}
                              label="Header format"
                              onChange={(e) => {
                                const next = [...carouselCards];
                                next[cIdx] = { ...next[cIdx], header_format: e.target.value };
                                setCarouselCards(next);
                              }}
                            >
                              <MenuItem value="image">Image</MenuItem>
                              <MenuItem value="video">Video</MenuItem>
                            </Select>
                          </FormControl>
                          <TextField
                            label="Media URL"
                            value={card.media_url || ""}
                            onChange={(e) => {
                              const next = [...carouselCards];
                              next[cIdx] = { ...next[cIdx], media_url: e.target.value, mediaFile: null };
                              setCarouselCards(next);
                            }}
                            error={!!errors[`carousel_media_${cIdx}`]}
                            helperText={errors[`carousel_media_${cIdx}`] || "Public URL to uploaded media"}
                            fullWidth
                            size="small"
                          />
                          <Button variant="outlined" component="label" size="small" sx={{ alignSelf: "flex-start" }}>
                            {card.mediaFile ? "Change card media" : "Upload card media"}
                            <input
                              type="file"
                              accept={card.header_format === "video" ? ".mp4" : ".jpg,.jpeg,.png"}
                              hidden
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const next = [...carouselCards];
                                next[cIdx] = { ...next[cIdx], mediaFile: file };
                                setCarouselCards(next);
                              }}
                            />
                          </Button>
                          {(card.mediaFile || card.media_url) && (
                            <Typography variant="caption" color="text.secondary">
                              {card.mediaFile?.name || card.media_url}
                            </Typography>
                          )}
                          {(card.mediaFile || card.media_url) && (
                            <Box
                              sx={{
                                p: 1,
                                border: "1px dashed",
                                borderColor: "divider",
                                borderRadius: 1,
                                bgcolor: "grey.50",
                              }}
                            >
                              {card.mediaFile ? (
                                <Stack direction="row" spacing={1} alignItems="center">
                                  {card.header_format === "video" ? <VideoFileIcon fontSize="small" /> : <ImageIcon fontSize="small" />}
                                  <Typography variant="caption" color="text.secondary">
                                    Local file selected ({card.header_format})
                                  </Typography>
                                </Stack>
                              ) : isImageUrl(card.media_url || "") ? (
                                <Box
                                  component="img"
                                  src={card.media_url}
                                  alt={`Card ${cIdx + 1}`}
                                  sx={{ width: 96, height: 96, objectFit: "cover", borderRadius: 1 }}
                                />
                              ) : isVideoUrl(card.media_url || "") ? (
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <VideoFileIcon fontSize="small" />
                                  <Typography variant="caption" color="text.secondary">
                                    Video URL configured
                                  </Typography>
                                </Stack>
                              ) : (
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <DescriptionIcon fontSize="small" />
                                  <Typography variant="caption" color="text.secondary">
                                    Media URL configured
                                  </Typography>
                                </Stack>
                              )}
                            </Box>
                          )}
                          {errors[`carousel_buttons_${cIdx}`] && (
                            <Alert severity="error">{errors[`carousel_buttons_${cIdx}`]}</Alert>
                          )}
                          {(card.buttons || []).map((btn, bIdx) => (
                            <Card key={`${cIdx}-${bIdx}`} variant="outlined" sx={{ p: 1.5 }}>
                              <Stack spacing={1.5}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Chip size="small" label={`Button ${bIdx + 1}`} />
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const next = [...carouselCards];
                                      const curBtns = [...(next[cIdx].buttons || [])];
                                      next[cIdx].buttons = curBtns.filter((_, i) => i !== bIdx);
                                      setCarouselCards(next);
                                    }}
                                    disabled={(card.buttons || []).length <= 1}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Type</InputLabel>
                                  <Select
                                    value={btn.type || "website"}
                                    label="Type"
                                    onChange={(e) => {
                                      const next = [...carouselCards];
                                      const curBtns = [...(next[cIdx].buttons || [])];
                                      curBtns[bIdx] = { ...curBtns[bIdx], type: e.target.value };
                                      next[cIdx].buttons = curBtns;
                                      setCarouselCards(next);
                                    }}
                                  >
                                    <MenuItem value="website">Website</MenuItem>
                                    <MenuItem value="phone">Phone</MenuItem>
                                    <MenuItem value="quick_reply">Quick reply</MenuItem>
                                  </Select>
                                </FormControl>
                                <TextField
                                  label="Button label"
                                  value={btn.label || ""}
                                  onChange={(e) => {
                                    const next = [...carouselCards];
                                    const curBtns = [...(next[cIdx].buttons || [])];
                                    curBtns[bIdx] = { ...curBtns[bIdx], label: e.target.value };
                                    next[cIdx].buttons = curBtns;
                                    setCarouselCards(next);
                                  }}
                                  error={!!errors[`carousel_btn_label_${cIdx}_${bIdx}`]}
                                  helperText={errors[`carousel_btn_label_${cIdx}_${bIdx}`]}
                                  size="small"
                                  fullWidth
                                />
                                {btn.type !== "quick_reply" && (
                                  <TextField
                                    label={btn.type === "phone" ? "Phone number" : "Website URL"}
                                    value={btn.value || ""}
                                    onChange={(e) => {
                                      const next = [...carouselCards];
                                      const curBtns = [...(next[cIdx].buttons || [])];
                                      curBtns[bIdx] = { ...curBtns[bIdx], value: e.target.value };
                                      next[cIdx].buttons = curBtns;
                                      setCarouselCards(next);
                                    }}
                                    error={!!errors[`carousel_btn_value_${cIdx}_${bIdx}`]}
                                    helperText={
                                      errors[`carousel_btn_value_${cIdx}_${bIdx}`]
                                      || (btn.type === "phone" ? "Example: +12025550123" : "Example: https://example.com/p/{{1}}")
                                    }
                                    size="small"
                                    fullWidth
                                  />
                                )}
                                {btn.type === "website" && /\{\{1\}\}/.test(btn.value || "") && (
                                  <TextField
                                    label="Sample URL for Meta"
                                    value={btn.urlExample || ""}
                                    onChange={(e) => {
                                      const next = [...carouselCards];
                                      const curBtns = [...(next[cIdx].buttons || [])];
                                      curBtns[bIdx] = { ...curBtns[bIdx], urlExample: e.target.value };
                                      next[cIdx].buttons = curBtns;
                                      setCarouselCards(next);
                                    }}
                                    error={!!errors[`carousel_btn_example_${cIdx}_${bIdx}`]}
                                    helperText={errors[`carousel_btn_example_${cIdx}_${bIdx}`] || "Full example URL when using {{1}}"}
                                    size="small"
                                    fullWidth
                                  />
                                )}
                              </Stack>
                            </Card>
                          ))}
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={(card.buttons || []).length >= CAROUSEL_BUTTON_MAX}
                            onClick={() => {
                              const next = [...carouselCards];
                              const curBtns = [...(next[cIdx].buttons || [])];
                              curBtns.push({ type: "website", label: "", value: "", urlExample: "" });
                              next[cIdx].buttons = curBtns;
                              setCarouselCards(next);
                            }}
                          >
                            Add card button
                          </Button>
                        </Stack>
                      </Card>
                    ))}
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={carouselCards.length >= CAROUSEL_CARD_MAX}
                      onClick={() => setCarouselCards((p) => [...p, {
                        header_format: "image",
                        media_url: "",
                        mediaFile: null,
                        buttons: [{ type: "website", label: "", value: "", urlExample: "" }],
                      }])}
                    >
                      Add card
                    </Button>
                  </Stack>,
                )
              ) : (isMarketingCategory && (templateLayout === "product_single" || templateLayout === "product_multi" || templateLayout === "product_carousel")) ? (
                renderSection(
                  "Catalog products",
                  "Select products from catalog configured in WhatsApp Settings.",
                  <Stack spacing={2}>
                    {!catalogId ? (
                      <Alert severity="warning">
                        Connect catalog first in WhatsApp Settings, then come back to create product templates.
                      </Alert>
                    ) : (
                      <Alert severity="info">Connected catalog: {catalogId}</Alert>
                    )}
                    {!catalogId && errors.catalogId && <Alert severity="error">{errors.catalogId}</Alert>}
                    <FormControl fullWidth size="small">
                      <Button
                        variant="outlined"
                        onClick={() => setProductDialogOpen(true)}
                        disabled={!catalogId}
                        sx={{ justifyContent: "flex-start", textTransform: "none" }}
                      >
                        {activeProductIds.length > 0 ? `${activeProductIds.length} products selected` : "Select products"}
                      </Button>
                    </FormControl>
                    <TextField
                      label="Search products"
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      size="small"
                      fullWidth
                      helperText="Quick filter for selected products list below"
                      disabled={!catalogId}
                    />
                    {activeProductIds.length > 0 && (
                      <Box sx={{ maxHeight: 160, overflow: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1 }}>
                        {activeProductIds
                          .filter((pid) => pid.toLowerCase().includes(productSearch.toLowerCase()))
                          .map((pid) => (
                            <Chip key={pid} size="small" label={pid} sx={{ mr: 0.5, mb: 0.5 }} />
                          ))}
                      </Box>
                    )}
                    {templateLayout === "product_single" && errors.productSingle && <Alert severity="error">{errors.productSingle}</Alert>}
                    {templateLayout === "product_multi" && errors.productMulti && <Alert severity="error">{errors.productMulti}</Alert>}
                    {templateLayout === "product_carousel" && errors.productCarousel && <Alert severity="error">{errors.productCarousel}</Alert>}
                    <Typography variant="caption" color="text.secondary">
                      {templateLayout === "product_single" && "Choose exactly one product."}
                      {templateLayout === "product_multi" && `Choose 2 to ${PRODUCT_MULTI_MAX} products for multi-product view.`}
                      {templateLayout === "product_carousel" && `Choose 2 to ${PRODUCT_CAROUSEL_MAX} products for carousel cards.`}
                    </Typography>
                  </Stack>,
                )
              ) : (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Meta allows either <strong>quick reply</strong> buttons or <strong>call-to-action</strong> (phone / website) in the same template — not both.
                  </Alert>
              {renderSection(
                "Button type",
                "Quick replies for in-chat choices; CTAs open a link or dial a number.",
                <RadioGroup
                  value={buttonGroup}
                  onChange={(e) => {
                    const g = e.target.value;
                    setButtonGroup(g);
                    if (g === "cta") setQuickReplyButtons([{ label: "" }]);
                    if (g === "quick") setCtaButtons([]);
                  }}
                >
                  <FormControlLabel value="none" control={<Radio />} label="No buttons" />
                  <FormControlLabel
                    value="quick"
                    control={<Radio />}
                    label={(
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <ReplyIcon fontSize="small" />
                        <span>Quick reply (max {QUICK_REPLY_MAX})</span>
                      </Stack>
                    )}
                  />
                  <FormControlLabel
                    value="cta"
                    control={<Radio />}
                    label={(
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <TouchAppIcon fontSize="small" />
                        <span>Call to action (phone + up to {CTA_URL_MAX} websites)</span>
                      </Stack>
                    )}
                  />
                </RadioGroup>,
              )}

              {buttonGroup === "quick" && renderSection(
                "Quick reply labels",
                `Up to ${QUICK_REPLY_MAX} short replies (${TEMPLATE_LIMITS.quickReplyLabel} characters each).`,
                <Stack spacing={2}>
                  {errors.quickReply && (
                    <Alert severity="error">{errors.quickReply}</Alert>
                  )}
                  {quickReplyButtons.map((row, idx) => (
                    <Stack key={idx} direction="row" spacing={1} alignItems="center">
                      <TextField
                        label={`Quick reply ${idx + 1}`}
                        value={row.label}
                        onChange={(e) => {
                          const next = [...quickReplyButtons];
                          next[idx] = { label: e.target.value };
                          setQuickReplyButtons(next);
                        }}
                        error={!!errors[`qr_${idx}`]}
                        helperText={errors[`qr_${idx}`]}
                        size="small"
                        fullWidth
                        inputProps={{ maxLength: TEMPLATE_LIMITS.quickReplyLabel }}
                      />
                      <IconButton
                        size="small"
                        aria-label="Remove"
                        onClick={() => setQuickReplyButtons((prev) => prev.filter((_, i) => i !== idx))}
                        disabled={quickReplyButtons.length <= 1}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={quickReplyButtons.length >= QUICK_REPLY_MAX}
                    onClick={() => setQuickReplyButtons((p) => [...p, { label: "" }])}
                  >
                    Add quick reply
                  </Button>
                </Stack>,
              )}

              {buttonGroup === "cta" && renderSection(
                "Call-to-action buttons",
                "Add up to one phone number and two website URLs. Use {{1}} once in a URL for a dynamic path; provide a full sample URL for Meta.",
                <Stack spacing={2}>
                  {errors.cta && <Alert severity="error">{errors.cta}</Alert>}
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<TouchAppIcon />}
                      disabled={ctaButtons.some((b) => b.type === "phone")}
                      onClick={() => setCtaButtons((p) => [...p, { type: "phone", label: "", value: "" }])}
                    >
                      Add phone
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<OpenInNewIcon />}
                      disabled={ctaButtons.filter((b) => b.type === "website").length >= CTA_URL_MAX}
                      onClick={() => setCtaButtons((p) => [...p, {
                        type: "website",
                        label: "",
                        value: "",
                        urlExample: "",
                      }])}
                    >
                      Add website
                    </Button>
                  </Stack>
                  <Divider />
                  {ctaButtons.map((btn, idx) => (
                    <Card key={idx} variant="outlined" sx={{ p: 2 }}>
                      <Stack spacing={2}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Chip
                            size="small"
                            label={btn.type === "phone" ? "Phone" : "Website"}
                            color={btn.type === "phone" ? "primary" : "secondary"}
                          />
                          <IconButton size="small" onClick={() => setCtaButtons((p) => p.filter((_, i) => i !== idx))}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                        <TextField
                          label="Button label"
                          value={btn.label}
                          onChange={(e) => {
                            const u = [...ctaButtons];
                            u[idx].label = e.target.value;
                            setCtaButtons(u);
                          }}
                          error={!!errors[`cta_label_${idx}`]}
                          helperText={errors[`cta_label_${idx}`]}
                          size="small"
                          inputProps={{ maxLength: TEMPLATE_LIMITS.ctaLabel }}
                        />
                        <TextField
                          label={btn.type === "phone" ? "E.164 phone number" : "Website URL (use {{1}} for one dynamic segment)"}
                          value={btn.value}
                          onChange={(e) => {
                            const u = [...ctaButtons];
                            u[idx].value = e.target.value;
                            setCtaButtons(u);
                          }}
                          error={!!errors[`cta_value_${idx}`]}
                          helperText={
                            errors[`cta_value_${idx}`]
                            || (btn.type === "website"
                              ? "Example: https://example.com/track/{{1}}"
                              : "Example: +12025550123")
                          }
                          size="small"
                          fullWidth
                        />
                        {btn.type === "website" && /\{\{1\}\}/.test(btn.value || "") && (
                          <TextField
                            label="Sample URL for Meta (required when using {{1}})"
                            value={btn.urlExample || ""}
                            onChange={(e) => {
                              const u = [...ctaButtons];
                              u[idx].urlExample = e.target.value;
                              setCtaButtons(u);
                            }}
                            error={!!errors[`cta_example_${idx}`]}
                            helperText={errors[`cta_example_${idx}`] || "Full resolved example, e.g. https://example.com/track/ORDER123"}
                            size="small"
                            fullWidth
                          />
                        )}
                      </Stack>
                    </Card>
                  ))}
                  {ctaButtons.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No CTA buttons yet — use Add phone or Add website.
                    </Typography>
                  )}
                </Stack>,
              )}

              {buttonGroup === "none" && (
                <Alert severity="success" variant="outlined">
                  No interactive buttons — body + optional header/footer only.
                </Alert>
              )}
                </>
              )}
            </>
          )}
        </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: 1,
          borderColor: "divider",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Button onClick={handleClose} variant="outlined" disabled={isSubmitting} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: "auto" }}>
          <Button
            onClick={handleBack}
            variant="outlined"
            disabled={isSubmitting || step === 0}
            startIcon={<ArrowBackIcon />}
            sx={{ textTransform: "none" }}
          >
            Back
          </Button>
          {!isLastStep && (
            <Button
              onClick={handleNext}
              variant={isEdit ? "outlined" : "contained"}
              disabled={isSubmitting}
              endIcon={<ArrowForwardIcon />}
              sx={{ textTransform: "none" }}
            >
              Next
            </Button>
          )}
          {isEdit && !isLastStep && (
            <Button
              onClick={handleSaveAppUsage}
              variant="contained"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              sx={{ textTransform: "none", px: 2.5 }}
            >
              {isSubmitting ? "Saving…" : "Save"}
            </Button>
          )}
          {isLastStep && (
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              sx={{ textTransform: "none", px: 2.5 }}
            >
              {isSubmitting ? "Saving…" : "Save template"}
            </Button>
          )}
        </Stack>
      </DialogActions>

      <Dialog open={productDialogOpen} onClose={() => setProductDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Select products</DialogTitle>
        <DialogContent>
          <TextField
            label="Search products"
            value={productDialogSearch}
            onChange={(e) => setProductDialogSearch(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            helperText={`Selected ${activeProductIds.length} / ${maxSelectableProducts}`}
          />
          <Box sx={{ maxHeight: 420, overflow: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
            {dialogFilteredCatalogProducts.map((p) => {
              const pid = String(p.catalog_product_id || p.product_id || p.id || p.retailer_id);
              const title = p.name || p.title || p.product_name || pid;
              const sku = p.sku || p.product_sku || "";
              const checked = activeProductIds.includes(pid);
              const disabled = !checked && activeProductIds.length >= maxSelectableProducts;
              return (
                <Box
                  key={pid}
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap>{title}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {pid}{sku ? ` • ${sku}` : ""}
                    </Typography>
                  </Box>
                  <Checkbox
                    checked={checked}
                    disabled={disabled}
                    onChange={() => toggleProductSelection(pid)}
                  />
                </Box>
              );
            })}
            {dialogFilteredCatalogProducts.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                No products found.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProductDialogOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default CreateTemplateDialog;
